import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Wallet, ShieldAlert, Save, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { api } from "../../lib/api";

/**
 * Fase 1 · Payment methods toggle — pantalla admin para activar/desactivar
 * los métodos de pago (tarjeta Stripe / efectivo contra entrega) sin
 * redeploy. Los guards server-side viven en:
 *  · POST /api/payments/order/checkout
 *  · POST /api/payments/shipment/checkout
 *  · POST /api/shipments (para efectivo)
 * Este panel solo edita el singleton `site_settings.payment_methods`.
 */
export default function AdminPaymentsSettings() {
  const [doc, setDoc] = useState(null);
  const [card, setCard] = useState(true);
  const [cash, setCash] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/payments");
      setDoc(data || null);
      setCard(!!data?.card_enabled);
      setCash(!!data?.cash_enabled);
    } catch (e) {
      toast.error("No se pudo cargar la configuración de pagos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () =>
      !doc ||
      doc.card_enabled !== card ||
      doc.cash_enabled !== cash,
    [doc, card, cash]
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/payments", {
        card_enabled: card,
        cash_enabled: cash,
      });
      setDoc(data);
      setCard(!!data.card_enabled);
      setCash(!!data.cash_enabled);
      toast.success("Métodos de pago actualizados");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo guardar";
      toast.error(typeof msg === "string" ? msg : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const formatUpdatedAt = (iso) => {
    if (!iso) return "Sin cambios registrados";
    try {
      return new Date(iso).toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (_e) {
      return iso;
    }
  };

  const bothOff = !card && !cash;

  return (
    <div
      data-testid="admin-payments-settings"
      className="max-w-3xl space-y-6"
    >
      <div>
        <h1 className="text-3xl font-black tracking-tight">
          Métodos de pago
        </h1>
        <p className="mt-2 text-sm text-neutral-600">
          Activa o desactiva los métodos de pago disponibles en el checkout.
          Los cambios entran en vigor de inmediato tanto para pedidos como
          para envíos de paquetes.
        </p>
      </div>

      {bothOff && (
        <div
          data-testid="admin-payments-warning-both-off"
          className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <strong>Todos los métodos de pago están desactivados.</strong>{" "}
            Los clientes no podrán completar pedidos ni envíos hasta que
            actives al menos uno.
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-6">
        {loading ? (
          <div className="h-24 animate-pulse rounded-xl bg-neutral-100" />
        ) : (
          <>
            {/* Tarjeta (Stripe) */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-10 w-10 shrink-0 rounded-xl bg-blue-50 grid place-items-center">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <Label
                    htmlFor="pm-card"
                    className="text-base font-bold cursor-pointer"
                  >
                    Tarjeta (Stripe)
                  </Label>
                  <p className="mt-0.5 text-sm text-neutral-500">
                    Pago online seguro con tarjeta bancaria vía Stripe.
                  </p>
                </div>
              </div>
              <Switch
                id="pm-card"
                checked={card}
                onCheckedChange={setCard}
                data-testid="admin-payments-card-switch"
                aria-label="Activar pagos con tarjeta"
              />
            </div>

            <div className="h-px bg-neutral-100" />

            {/* Efectivo */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 h-10 w-10 shrink-0 rounded-xl bg-emerald-50 grid place-items-center">
                  <Wallet className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <Label
                    htmlFor="pm-cash"
                    className="text-base font-bold cursor-pointer"
                  >
                    Efectivo contra entrega
                  </Label>
                  <p className="mt-0.5 text-sm text-neutral-500">
                    El cliente paga en metálico cuando el mensajero recoge
                    el paquete.
                  </p>
                </div>
              </div>
              <Switch
                id="pm-cash"
                checked={cash}
                onCheckedChange={setCash}
                data-testid="admin-payments-cash-switch"
                aria-label="Activar pago en efectivo"
              />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-neutral-500" data-testid="admin-payments-meta">
          <div>
            Actualizado:{" "}
            <span className="font-semibold text-neutral-700">
              {formatUpdatedAt(doc?.updated_at)}
            </span>
          </div>
          <div>
            Por:{" "}
            <span className="font-semibold text-neutral-700">
              {doc?.updated_by || "—"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={load}
            disabled={loading || saving}
            data-testid="admin-payments-refresh-btn"
            className="rounded-full"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Recargar
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="admin-payments-save-btn"
            className="rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <Save className="mr-1 h-4 w-4" />
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </div>
    </div>
  );
}
